import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { FiVolume2, FiMessageCircle, FiHeart, FiShoppingCart, FiSliders } from 'react-icons/fi';

/**
 * ContentGoalSelector - User-friendly content goal selection component
 * 
 * Maps to V.E.E. (Value + Entertainment + Emotion) framework optimization.
 * Users select their business goal, we translate to content strategy.
 */

// Icon mapping for goals
const GOAL_ICONS = {
  discover: FiVolume2,
  community: FiMessageCircle,
  superfans: FiHeart,
  action: FiShoppingCart,
  balanced: FiSliders
};

// Fallback config if API is not available
const FALLBACK_GOALS = [
  {
    value: 'discover',
    label: 'Get Discovered',
    description: 'I want more people to know we exist',
    best_for: 'New businesses, launches, announcements',
    icon: '🔊',
    optimize_for: 'reach'
  },
  {
    value: 'community',
    label: 'Build My Community',
    description: 'I want people who engage and come back',
    best_for: 'Regulars, loyalty, local businesses',
    icon: '💬',
    optimize_for: 'engagement'
  },
  {
    value: 'superfans',
    label: 'Create Superfans',
    description: 'I want content people save and share',
    best_for: 'Premium brands, word-of-mouth growth',
    icon: '💾',
    optimize_for: 'resonance'
  },
  {
    value: 'action',
    label: 'Drive Action',
    description: 'I want people to buy/book/visit',
    best_for: 'Promotions, limited offers, events',
    icon: '🛒',
    optimize_for: 'conversion'
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Let AI optimize the mix',
    best_for: 'General content, ongoing presence',
    icon: '⚖️',
    optimize_for: 'balanced'
  }
];

export function ContentGoalSelector({ value, onChange, className = '' }) {
  const [goals, setGoals] = useState(FALLBACK_GOALS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const response = await api.getContentGoals();
        if (response.goals && response.goals.length > 0) {
          setGoals(response.goals);
        }
      } catch (error) {
        console.warn('Failed to fetch content goals, using fallback:', error);
        // Use fallback goals
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, []);

  const handleSelect = (goalValue) => {
    onChange(goalValue);
  };

  if (loading) {
    return (
      <div className={`animate-pulse space-y-3 ${className}`}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 bg-muted/30 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`} data-testid="content-goal-selector">
      <div className="text-sm text-muted-foreground mb-4">
        What's your goal for this campaign's content?
      </div>
      
      {goals.map((goal) => {
        const Icon = GOAL_ICONS[goal.value] || FiSliders;
        const isSelected = value === goal.value;
        
        return (
          <button
            key={goal.value}
            type="button"
            onClick={() => handleSelect(goal.value)}
            data-testid={`content-goal-${goal.value}`}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
              isSelected
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                : 'border-border/50 bg-card hover:border-primary/30 hover:bg-muted/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${
                isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                <Icon size={20} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                    {goal.label}
                  </span>
                  {isSelected && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                      Selected
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground mt-1">
                  "{goal.description}"
                </p>
                
                <p className="text-xs text-muted-foreground/70 mt-2">
                  Best for: {goal.best_for}
                </p>
              </div>
            </div>
          </button>
        );
      })}
      
      <p className="text-xs text-muted-foreground/60 mt-4 text-center">
        This helps our AI optimize your content for {value === 'discover' ? 'maximum reach' : 
          value === 'community' ? 'engagement' :
          value === 'superfans' ? 'memorable, shareable content' :
          value === 'action' ? 'driving conversions' : 'balanced performance'}.
      </p>
    </div>
  );
}

// Compact version for inline use
export function ContentGoalBadge({ value, showLabel = true }) {
  const goal = FALLBACK_GOALS.find(g => g.value === value) || FALLBACK_GOALS[4];
  const Icon = GOAL_ICONS[value] || FiSliders;
  
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-md text-sm">
      <Icon size={14} className="text-muted-foreground" />
      {showLabel && <span className="text-muted-foreground">{goal.label}</span>}
    </div>
  );
}

export default ContentGoalSelector;
